import hash from 'object-hash';
import axios from 'axios';
import update from 'immutability-helper';
import { decode } from 'jsonwebtoken';
import { requestInterceptor } from './APIInterceptors';
import { getItem } from './LocalStorage';

const pendingRequests = new Map();
const { CancelToken } = axios;

const axiosInstance = axios.create();
const flagsByType = {
  request: false,
};

export const tokenIsExpired = (token) => {
  const decoded = decode(token);
  const now = Math.floor(Date.now() / 1000);

  // Decoded returns null if token is not valid
  // 2 minute buffer so token doesn't expire before request
  return !decoded || now > decoded.exp - 120;
};

const waitingRequests = [];

const processQueue = (error, token = null) => {
  const nextRequest = waitingRequests.pop();
  if (nextRequest) {
    if (error) {
      return nextRequest.reject(error);
    }
    return nextRequest.resolve(token);
  }
  return true;
};

const refreshToken = (url, data = {}, options = {}) => (
  new Promise((resolve, reject) => axios.post(url, data, options).then((response) => {
    resolve(response.data);
  }).catch((error) => {
    reject(error);
  })));

const getUrlDomain = (url = '') => {
  const splitUrl = url.split('/');
  return splitUrl.length >= 2 ? `${splitUrl[0]}//${splitUrl[2]}` : null;
};

requestInterceptor(axiosInstance, getUrlDomain, tokenIsExpired, refreshToken, flagsByType, waitingRequests);

// ------------------------------------
// Request
// ------------------------------------
const request = (url, options = {}, hashableBody) => {
  const key = hash({ url, options: hashableBody ? update(options, { body: { $set: hashableBody } }) : options });
  const removePendingRequest = () => {
    // Unmark an ongoin request
    flagsByType.request = false;
    processQueue(null, getItem('API_TOKEN'));
    pendingRequests.delete(key);
  };
  const source = CancelToken.source();

  if (pendingRequests.has(key)) return pendingRequests.get(key).promise;

  const promise = axiosInstance({ url, cancelToken: source.token, ...options });

  promise.then(removePendingRequest, removePendingRequest);

  pendingRequests.set(key, {
    url,
    options,
    promise,
    source,
  });

  return promise;
};

export const abortAllPendingRequests = () => {
  pendingRequests.forEach((req) => {
    req.source.cancel('Operation canceled by the user.');
  });
  pendingRequests.clear();
  waitingRequests.splice(0, waitingRequests.length);
};

export const wasRequestCancelled = (thrown) => axios.isCancel(thrown);

export default request;
